import React from 'react';
import { i18n } from 'config/locales';
import { IDENTIFICATION, IDENTIFICATION_SSN } from 'config/formSections/identification';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import Subsection, { SubsectionProps } from 'components/Section/shared/Subsection';
import Field from 'components/Form/Field';
import Show from 'components/Form/Show';
import SSN from 'components/Form/SSN';
import Textarea from 'components/Form/Textarea';
import { IdentificationSSNModel } from 'forms/sections/identification/ssn/types';
import { CommonOnUpdate, CommonRequired, CommonScrollIntoView } from 'types/props';
import { SSNModel } from 'forms/shared/types';
import { isPVQForm } from 'helpers/form';
import { validSSN } from '../../../../validators/helpers';

const sectionConfig = {
  key: IDENTIFICATION_SSN.key,
  section: IDENTIFICATION.name,
  store: IDENTIFICATION.store,
  subsection: IDENTIFICATION_SSN.name,
  storeKey: IDENTIFICATION_SSN.storeKey,
};

interface Props extends SubsectionProps, Partial<IdentificationSSNModel> {
  onUpdate?: CommonOnUpdate
  className?: string
  backRef?: React.RefObject<HTMLAnchorElement>
  scrollIntoView?: CommonScrollIntoView
  required?: CommonRequired
}

interface State {
  uid: string
  verification: any
  error: boolean
  verified: boolean
}

export class ApplicantSSN extends Subsection<Props, State> {
  constructor(props: Props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;

    this.state = {
      uid: `${subsection}-${super.guid()}`,
      verification: {},
      error: false,
      verified: props.verified || false,
    };
    this.updateVerification = this.updateVerification.bind(this);
    this.updateSSN = this.updateSSN.bind(this);
    this.updateSSNNAExplanation = this.updateSSNNAExplanation.bind(this);
    this.verificationError = this.verificationError.bind(this);
  }

  update = (queue: Partial<IdentificationSSNModel>) => {
    this.props.onUpdate(this.storeKey, {
      ssn: this.props.ssn,
      verifiedSSN: this.props.verifiedSSN,
      verified: this.props.verified,
      SSNNAExplanation: this.props.SSNNAExplanation,
      ...queue,
    });
  };

  updateSSN = (values: IdentificationSSNModel['ssn']) => {
    this.update({
      ssn: values,
      error: false,
    });
  };

  updateSSNNAExplanation = (values: IdentificationSSNModel['SSNNAExplanation']) => {
    this.update({
      SSNNAExplanation: values,
    });
  };

  componentDidUpdate(prevProps: Props) {
    const { ssn, verified } = this.props;

    if (prevProps.verified !== verified && verified) {
      this.props.backRef?.current?.focus();
    }

    if (
      ssn.first !== prevProps.ssn.first
      || ssn.middle !== prevProps.ssn?.middle
      || ssn.last !== prevProps.ssn.last
    ) {
      this.update({
        verified: false,
        verifiedSSN: {
          first: '',
          middle: '',
          last: '',
          notApplicable: false,
        },
      });
    }
  }

  updateVerification = (values: SSNModel) => {
    const verified = this.props.ssn?.first === values.first
      && this.props.ssn?.middle === values.middle
      && this.props.ssn?.last === values.last
      && validSSN(values);

    this.setState(
      (prevState) => ({
        verified,
        verification: verified ? {} : values,
        error: verified ? false : prevState.error,
      }),
      () => {
        // Update the properties so they can be reflected
        // within this component.
        this.update({ verified, verifiedSSN: values });
      }
    );
  };

  verificationError = (value, arr) => {
    // If the verification SSN is valid then we run our validation
    // function(s). However, if it is not then we set the `valid` property
    // to a neutral state. This allows the error notifications to be properly
    // toggled within the various subscribing components.
    const { verification } = this.state;
    const local = this.props.onError(
      value,
      this.constructor.errors.map((err) => ({
        code: err.code,
        valid: validSSN(verification) ? err.func(verification, this.props) : null,
        uid: this.state.uid,
      }))
    ) || [];

    // Set the error state value so we can apply a CSS class to
    // the entire SSN.
    this.setState({ error: local.some((x) => x.valid === false) });
    return this.handleError(value, arr.concat(local));
  };

  render() {
    const klass = `section-content applicant-ssn ${this.props.className || ''}`.trim();
    const klassVerify = `applicant-ssn-verification ${this.state.error ? 'usa-input-error' : ''
    }`.trim();
    const verify = validSSN(this.props.ssn) && !this.props.verified && !this.props.ssn?.notApplicable;
    const isPVQ = isPVQForm(this.props.formType);

    return isPVQ ? (
      <div
        className={klass}
        data-section={IDENTIFICATION.key}
        data-subsection={IDENTIFICATION_SSN.key}
      >
        <h1 className="section-header">{i18n.t('identification.pvq.destination.ssn')}</h1>
        <label className="ssn-label">{i18n.m('identification.pvq.ssn.info')}</label>
        <Field
          title={i18n.t('identification.pvq.ssn.title')}
          titleSize="h4"
          help="identification.pvq.ssn.help"
          scrollIntoView={this.props.scrollIntoView}
          attribute="ssn"
          className="minimal-margin-bottom"
          exactAttribute
        >
          <SSN
            attribute="ssn"
            name="ssn"
            {...this.props.ssn}
            className="applicant-ssn-initial"
            onUpdate={this.updateSSN} // eslint-disable-line @typescript-eslint/unbound-method
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            required={!this.props.ssn?.notApplicable && this.props.required}
          />
        </Field>
        <Show when={this.props.ssn?.notApplicable}>
          <Field
            title="Please explain."
            titleSize="label"
            scrollIntoView={this.props.scrollIntoView}
            attribute="SSNNAExplanation"
          >
            <Textarea
              name="SSNNAExplanation"
              className="ssn-na-explanation"
              {...this.props.SSNNAExplanation}
              onUpdate={this.updateSSNNAExplanation}
              onError={this.handleError}
              required={this.props.required}
              attribute="SSNNAExplanation"
            />
          </Field>
        </Show>

        <Show when={verify}>
          <Field
            title={i18n.t('identification.pvq.ssn.heading.verify')}
            scrollIntoView={this.props.scrollIntoView}
            titleSize="h4"
            attribute="verified"
          >
            <SSN
              attribute="verifiedSSN"
              name="verification"
              {...this.props.verifiedSSN}
              hideNotApplicable
              className={klassVerify}
              onUpdate={this.updateVerification} // eslint-disable-line @typescript-eslint/unbound-method
              onError={this.verificationError} // eslint-disable-line @typescript-eslint/unbound-method
              required={verify && this.props.required}
            />
          </Field>
        </Show>
        <Show when={this.props.verified}>
          <Field
            title={i18n.t('identification.pvq.ssn.heading.verified')}
            optional
            scrollIntoView={this.props.scrollIntoView}
            titleSize="h4"
          />
        </Show>
      </div>
    ) : (
      <div
        className={klass}
        data-section={IDENTIFICATION.key}
        data-subsection={IDENTIFICATION_SSN.key}
      >
        <h1 className="section-header">{i18n.t('identification.destination.ssn')}</h1>
        <Field
          title={i18n.t('identification.ssn.title')}
          titleSize="h4"
          help="identification.ssn.help"
          scrollIntoView={this.props.scrollIntoView}
          attribute="ssn"
        >
          <SSN
            attribute="ssn"
            name="ssn"
            {...this.props.ssn}
            className="applicant-ssn-initial"
            onUpdate={this.updateSSN} // eslint-disable-line @typescript-eslint/unbound-method
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            required={!this.props.ssn?.notApplicable && this.props.required}
          />
        </Field>

        <Show when={verify}>
          <Field
            title={i18n.t('identification.ssn.heading.verify')}
            scrollIntoView={this.props.scrollIntoView}
            titleSize="h4"
            attribute="verified"
          >
            <SSN
              attribute="verifiedSSN"
              name="verification"
              {...this.props.verifiedSSN}
              hideNotApplicable
              className={klassVerify}
              onUpdate={this.updateVerification} // eslint-disable-line @typescript-eslint/unbound-method
              onError={this.verificationError} // eslint-disable-line @typescript-eslint/unbound-method
              required={verify && this.props.required}
            />
          </Field>
        </Show>
        <Show when={this.props.verified}>
          <Field
            title={i18n.t('identification.ssn.heading.verified')}
            optional
            scrollIntoView={this.props.scrollIntoView}
            titleSize="h4"
          />
        </Show>
      </div>
    );
  }
}

ApplicantSSN.defaultProps = {
  ssn: {},
  verifiedSSN: {},
  verified: false,
  SSNNAExplanation: {},
  onUpdate: () => { },
  onError: (value, arr) => arr,
  dispatch: () => { },
  required: false,
};

ApplicantSSN.errors = [
  {
    code: 'ssn.mismatch',
    func: (value, props) => {
      if (!value) {
        return null;
      }

      return (
        props.ssn.first === value.first
        && props.ssn.middle === value.middle
        && props.ssn.last === value.last
      );
    },
  },
];

export default connectSubsection(ApplicantSSN, sectionConfig);
